import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useLanguage } from '@/contexts/LanguageContext';
import { ResponsiveTable, TABLE_COL, type ResponsiveTableColumn } from '@/components/ui/responsive-table';

export interface TopPositionsRow {
  positionId: string;
  title: string;
  applied: number;
  awaiting: number;
  errors: number;
  rejected: number;
  rejectRate: number;
  /** Optional — present only on cross-portfolio (agency Plan view). */
  companyId?: string;
  companyName?: string;
}

interface TopPositionsTableProps {
  data: TopPositionsRow[];
  pipelineErrors: number;
  /** Optional custom title (defaults to t('dashboard.topPositions')). */
  title?: string;
  /**
   * Sprint dashboard_alignment_v1 (26/05/26 22:00). When agency Plan
   * view passes positions from multiple clients, this callback fires
   * on row click — the parent can switch the active company before
   * routing, otherwise the URL would resolve to the wrong tenant.
   */
  onCrossCompanyNavigate?: (row: TopPositionsRow) => void;
}

export const TopPositionsTable = ({
  data,
  pipelineErrors,
  title,
  onCrossCompanyNavigate,
}: TopPositionsTableProps) => {
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Show the company column only when at least one row carries a companyName
  // (cross-portfolio agency view). Stay collapsed for single-tenant SME view.
  const showCompanyCol = data.some(r => !!r.companyName);

  const columns: ResponsiveTableColumn<TopPositionsRow>[] = [
    {
      id: 'title',
      label: t('dashboard.position'),
      priority: 'primary',
      alwaysVisible: true,
      ...TABLE_COL.title,
      truncate: true,
      render: (row) => <span className="font-medium truncate block" title={row.title}>{row.title}</span>,
    },
    ...(showCompanyCol
      ? [{
          id: 'company',
          label: t('dashboard.client'),
          priority: 'secondary' as const,
          ...TABLE_COL.text,
          render: (row: TopPositionsRow) => (
            <span className="text-xs text-muted-foreground truncate block" title={row.companyName}>
              {row.companyName ?? '—'}
            </span>
          ),
        }]
      : []),
    {
      id: 'applied',
      label: t('dashboard.applied'),
      priority: 'secondary',
      ...TABLE_COL.count,
      alwaysVisible: true,
      render: (row) => <span className="tabular-nums">{row.applied}</span>,
    },
    {
      id: 'awaiting',
      label: t('dashboard.topAwaiting'),
      priority: 'secondary',
      ...TABLE_COL.count,
      alwaysVisible: true,
      render: (row) => (
        <span className={row.awaiting > 0 ? 'text-warning font-medium tabular-nums' : 'text-muted-foreground tabular-nums'}>
          {row.awaiting}
        </span>
      ),
    },
    {
      id: 'errors',
      label: t('dashboard.topErrors'),
      priority: 'tertiary',
      ...TABLE_COL.count,
      hideOn: 'tablet',
      render: (row) => (
        <span className={row.errors > 0 ? 'text-destructive font-medium tabular-nums' : 'text-muted-foreground tabular-nums'}>
          {row.errors}
        </span>
      ),
    },
    {
      id: 'rejected',
      label: t('dashboard.rejected'),
      priority: 'tertiary',
      ...TABLE_COL.count,
      hideOn: 'tablet',
      render: (row) => <span className="text-muted-foreground tabular-nums">{row.rejected}</span>,
    },
    {
      id: 'rejectRate',
      label: t('dashboard.rejectRate'),
      priority: 'tertiary',
      ...TABLE_COL.count,
      hideOn: 'mobile',
      render: (row) => (
        <span className={row.rejectRate > 80 ? 'text-destructive font-medium tabular-nums' : 'text-muted-foreground tabular-nums'}>
          {row.rejectRate.toFixed(0)}%
        </span>
      ),
    },
  ];

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium">{title ?? t('dashboard.topPositions')}</CardTitle>
          {pipelineErrors > 0 && (
            <span className="inline-flex items-center gap-1 rounded-md bg-destructive/10 text-destructive px-2 py-0.5 text-xs font-medium">
              {t('dashboard.issuesReview', { count: pipelineErrors })}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {data.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-8 px-5">{t('dashboard.noPositionsData')}</p>
        ) : (
          <div>
            <ResponsiveTable
              data={data}
              columns={columns}
              getRowKey={(row) => row.positionId}
              onRowClick={(row) => {
                if (row.companyId && onCrossCompanyNavigate) {
                  onCrossCompanyNavigate(row);
                } else {
                  navigate(`/job-positions/${row.positionId}`);
                }
              }}
              storageKey="dashboard-top-positions"
              ariaLabel={title ?? t('dashboard.topPositions')}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
